import React, { useCallback, useEffect, useState } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaDispatch, useAvivaSelector } from '@redux/hook';
import { isEmpty, get, find, set } from 'lodash';
import { GAAviva } from '@utils/gaAviva';
import { useHistory } from 'react-router-dom';
import { PaymentForm } from '@models/SIO/Payment.model';
import { Coverages, SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { PaymentRequest } from '@models/PaymentRequest.model';
import { updateReferenceNo, updatePolicyNo, updateBankId, updateCardHolderName, updateOtherBank, updatePaymentDone } from '@redux/slices/SIO/step5Slice';
import { setLoadingState } from '@redux/slices/loadingSlice';
import { getPolicyNo, postComputePremium, postPayment, savePayment, sendMail } from '@api/index';


import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import StickyBox from '@components/StickyBox';
import PremiumCost from '@components/PremiumCost';
import InformationBox from '@components/InformationBox';
import Text from '@components/Text';
import ContainerBlock from '@components/ContainerBlock';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import Checkbox from '@components/Checkbox';
import CheckboxList from '@components/CheckboxList';
import Link from '@components/Link';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import Modal from '@components/Modal';
import Quote from '@components/Quote';
import Bullet from '@components/Bullet';

import { validate } from './service';
import { css } from '@linaria/core';
import { Helpers } from '@utils/helpers';
import Divider from '@components/Divider';

const page = 'payment';

const BoldText = styled.span<{color: string}>`
  font-weight: 600;
  color: ${prop => prop.color};
`
const fluidBox = css`
  margin-top: -40px;
`;

const trackOnload = ({ payment = '', errorMessage = '', policyID = '', sumassured = '' }): void => {
  const GA = GAAviva.getInstance();
  let { page, acquisition, form } = GA.digitalData;
  page.errorMessage = errorMessage;
  acquisition.product.policyID = policyID;
  form.name = 'Payment';
  if (!isEmpty(payment)) {
    acquisition.insurance.paymentMethod = payment;
    acquisition.insurance.paymentOptions = 'Credit Card';
    acquisition.insurance.sumassured = sumassured;
  }
  GA.trackPageLoad({ page, acquisition, form });
}

const trackFormSubmmit = (error: string = ''): void =>  {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

const quoteInitialSample = [
  {
    name: 'Basic plan',
    details: [
      { text: 'Sum assured', value: '-' },
      { text: 'Premium term', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Add-on',
    details: [
      { text: 'Rider', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Premium term', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Other details',
    details: [
      { text: 'Premium payment frequency', value: '-' },
      { text: 'Promo code', value: '-' },
    ]
  }
]
export interface PaymentProps { }

function Payment(props: PaymentProps) {
  console.log(`Aviva - ${page}`);
  const theme: ITheme = useTheme();
  const history = useHistory();

  const dispatch = useAvivaDispatch();
  
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const banks = useAvivaSelector(state => state.staticData.banks);
  
  const sqsQuotation = useAvivaSelector(state => state.sioStep1.sqsQuotation);
  const quoteRawResponse = useAvivaSelector(state => state.sioStep1.quoteRawResponse);
  const quoteRawRequest = useAvivaSelector(state => state.sioStep1.quoteRawRequest);
  const basicDetails = useAvivaSelector(state => state.sioStep1.basicDetails);
  const promoCodeSummary = useAvivaSelector(state => state.sioStep1.appliedPromoCode);
  const typeUW = useAvivaSelector(state => state.sioStep3.typeUW);

  const occupation = useAvivaSelector(state => state.sioStep1.occupation);
  const personalDetails = useAvivaSelector(state => state.sioStep2.personalDetails);
  const retrieveMyInfo = useAvivaSelector(state => state.sioStep2.retrievedMyInfo);
  const referenceNo = useAvivaSelector(state => state.sioStep5.referenceNo);
  const isStep4Completed =  useAvivaSelector(state => state.sioStep4.isCompleted);
  const policyIllustrationPDFPath = useAvivaSelector(state => state.sioStep4.pdfPath);
  // const cardHolderName = useAvivaSelector(state => state.sioStep5.cardHolderName);
  const bankId = useAvivaSelector(state => state.sioStep5.bankId);
  const otherBank = useAvivaSelector(state => state.sioStep5.otherBank);
  const paymentDone = useAvivaSelector(state => state.sioStep5.paymentDone);
  const transactionId = useAvivaSelector(state=>state.sioStep1.transactionId)

  const [hasError, setError] = useState<boolean>(false);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [premium, setPremium] = useState<any>({});
  const [isShowPromoCode, setIsShowPromocode] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<any>(quoteInitialSample);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isStatusCheck, setIsStatusCheck] = useState<boolean>(false);

  const [isPaymentClick, setIsPaymentClick] = useState<boolean>(false);

  // const [paymentUrl, setPaymentUrl] = useState<any>('');
  // const urlParams: URLSearchParams = Helpers.getURLSearchParams();

  const { values, errors, handleChange, formValid } = useForm(new PaymentForm({
    no: get(personalDetails, 'nric', null), name: get(personalDetails, 'fullName', null)
  }), () => { }, validate);

  const setLoading = useCallback((payload: boolean) => {
    dispatch(setLoadingState(payload));
  }, [dispatch])

  const checkStatus = useCallback(() => {
    const onSuccessPayment = (payload: any) => {
      setLoading(true);
      getPolicyNo(payload, 1, retrieveMyInfo?.id).then((res: any) => {
        console.log('getPolicyNo', res);
        const policyNo = get(res, 'data');
        const quotation: SQSPocketResponse = new SQSPocketResponse(quoteRawResponse);
        const simplePlan: Coverages | undefined = find(quotation.coverages, { id: 'simpleTermPlan' });
        trackOnload({ policyID: policyNo, payment: Helpers.getBankName(`${bankId}`) || otherBank, sumassured: `${simplePlan?.attributes?.sumAssured || 0}` });
        dispatch(updatePolicyNo(String(policyNo)));
        sendMail(payload, 1, retrieveMyInfo?.id);
        // history.push('/sio/thank-you');
        setTimeout(() => {
          dispatch(updatePaymentDone(true));
          history.push('/sio/thank-you');
        }, 2000);
        setLoading(false);
      }, err => {
        console.error(err);
        setLoading(false);
      });
    }

    const onFailedPayment = (payload: any) => {
      trackFormSubmmit('Payment is unsuccessful');
      setLoading(false);
    }
    
    setLoading(true);
    const params = new URLSearchParams(window.location.href.split('?')[1]);
    const status = params.get('status');
    console.log('data saved referenceNo', referenceNo, status);
    if (!isStatusCheck) {
      if (referenceNo !== '' && status === 'processing') {
        setIsProcessing(true);
        dispatch(setLoadingState(true));
        console.log('is payment success');
        onSuccessPayment(referenceNo);
        trackFormSubmmit();
      }
      else if (status === 'unsuccessful') {
        setError(true);
        trackOnload({ policyID: 'policyNo', payment: Helpers.getBankName(`${bankId}`) || otherBank, errorMessage: 'Payment is unsuccessful' });
        onFailedPayment(referenceNo);
        setLoading(false);
      }
      else {
        trackOnload({});
        setLoading(false);
      }
      setIsStatusCheck(true);
      setLoading(false);
    } else {
      setLoading(false);
    }

  }, [referenceNo, setIsProcessing, dispatch, history, quoteRawResponse, setError, setLoading, retrieveMyInfo, isStatusCheck, bankId, otherBank])

  const validatePromoCode = useCallback(async () => {
    console.debug('validatePromoCode', promoCodeSummary);
    if (isEmpty(promoCodeSummary)) {
      return true;
    }
    if (typeUW === 'SIOB' && (promoCodeSummary === 'SIO1' || promoCodeSummary === 'SIO3')) {
      return true;
    }
    const payload = { ...quoteRawRequest };
    set(payload, 'coverages[0].attributes.promoCodeSummary', promoCodeSummary);
    const response = await postComputePremium(payload);
    const data = get(response, 'data.data', {});
    const pocket = new SQSPocketResponse(data);
    const checkedCode = pocket.checkValidPromoCode(promoCodeSummary);
    const { valid } = checkedCode;
    return valid;
  }, [quoteRawRequest, promoCodeSummary, typeUW]);

  const onSavePayment = useCallback(async (contractCode: string, paymentPayload: any, policyNo?: string) => {
    const quotation: SQSPocketResponse = new SQSPocketResponse(quoteRawResponse);
    const simplePlan: Coverages | undefined = find(quotation.coverages, { id: 'simpleTermPlan' });
    const illnessCoverPlan: Coverages | undefined = find(quotation.coverages, { id: 'lateStageAddCICover' });
    const personalAccidentPlan: Coverages | undefined = find(quotation.coverages, { id: 'personalAccidentRider' });
    // const totalPayablePremium = Number(quotation.sioPremium.totalPremium.value.toString().replace('S$', '')) || 1; // for testing
    const totalPayablePremium = (quotation.totalAftDisOriginalPremiumAmt) ? Helpers.toTwoDecimal(quotation.totalAftDisOriginalPremiumAmt) : Helpers.toTwoDecimal(quotation.totalOriginalPremiumAmt);

    console.debug('simplePlan', simplePlan, 'illnessCoverPlan', illnessCoverPlan, 'personalAccidentPlan', personalAccidentPlan, 'bankId', bankId);
    console.log('CHECK', values['bank'], Number(values['bank']));
    const payload = {
      policy: {
        // "policyNumber": policyNo,
        "contractCode": simplePlan?.productCode,
        "customerId": retrieveMyInfo?.id,
        "productId": 1,
        "planName": simplePlan?.name,
        "sumAssured": simplePlan?.attributes?.sumAssured || 0,
        "premiumTerm": '',
        "premiumAmount": simplePlan?.attributes?.modalPremium || 0,
        "riderCICover": illnessCoverPlan?.name,
        "sumAssuredCICover": illnessCoverPlan?.attributes?.sumAssured || 0,
        "premiumTermCICover": illnessCoverPlan?.attributes?.premiumTerm,
        "premiumAmountCICover": illnessCoverPlan?.attributes?.modalPremium || 0,
        "riderPACover": personalAccidentPlan?.name,
        "sumAssuredPACover": personalAccidentPlan?.attributes?.sumAssured || 0,
        "premiumTermPACover": personalAccidentPlan?.attributes?.premiumTerm,
        "premiumAmountPACover": personalAccidentPlan?.attributes?.modalPremium || 0,
        "premiumPaymentFrequency": simplePlan?.attributes?.planPremiumMode,
        "totalAnnualPremium": quotation.totalOriginalPremiumAmt || 0,
        "afterPromoDiscount": (quotation.totalAftDisOriginalPremiumAmt) ? quotation.totalAftDisOriginalPremiumAmt : quotation.totalOriginalPremiumAmt,
        "firstYearDiscount": quotation.totalFirstYearDiscount || 0,
        "perpetualDiscount": quotation.totalPerpetualDiscount || 0,
        "discountType": simplePlan?.attributes?.discountedPremium.firstYrDiscType === 'percent' ? 'percentage' : simplePlan?.attributes?.discountedPremium.firstYrDiscType === 'dollar' ? 'dollar' : '',
        "addONCoverage": '',
        "promoCode": simplePlan?.attributes?.promoCode,
        "campaignCode": '',
        "productCode": 'SIO',
        "quotationRespData": JSON.stringify(quoteRawResponse), //json str
        "sourceBusiness": '',
        "contractCurrency": 'SGD',
        "smokingStatus": basicDetails?.smoker,
        "occupationRespData": JSON.stringify(occupation),
        "policyIllustrationPDFPath": policyIllustrationPDFPath,
        "paymentMode": 'CC',
        "transactionId": transactionId
      },
      payment: {
        cardHolderNric: get(personalDetails, 'nric', null),
        cardHolderName: get(personalDetails, 'fullName', null),
        bankId: Number(values['bank']),
        paymentAmount: totalPayablePremium,
        // referenceNo: referenceNo, //reference no
        otherBank: values['others']
      }
    }
    try {
      dispatch(setLoadingState(true));
      const response = await savePayment(payload);
      const refNo = get(response, 'data', '');
      console.log('refno', refNo);
      console.log('savePayment', refNo);
      paymentPayload.referenceNumber = refNo;
      dispatch(updateReferenceNo(String(refNo)));

      postPayment(paymentPayload).then((res: any) => {
        console.debug('postPayment', res);
        const paymentUrl = get(res, 'data');
  
        console.log(paymentUrl.split('?')[1]);
        // const params = new URLSearchParams(paymentUrl.split('?')[1]);
        // const refNo = params.get('referenceNumber');
        dispatch(updateCardHolderName(values['name']));
        dispatch(updateBankId(Number(values['bank'])));
        dispatch(updateOtherBank(values['others']));
  
        const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + '#/dpi/payment?status=processing';
        console.log(fullPath);
        setLoading(true);
        window.open(paymentUrl, '_self');
      }).catch((err: any) => {
        setLoading(false);
        console.error(err);
        setError(true);
      });
    } catch (e) {
      dispatch(setLoadingState(false));
    }
    // savePayment(payload).then((res: any) => {
    //   console.log('savePayment', res);
    //   setTimeout(() => {
    //     history.push('/sio/thank-you');
    //   }, 2000);
    //   setLoading(false);
    // }, err => {
    //   console.error(err); 
    //   history.push('/sio/thank-you');
    //   setLoading(false);
    // });
  }, [bankId, basicDetails, dispatch, occupation, personalDetails, policyIllustrationPDFPath, quoteRawResponse, retrieveMyInfo, setLoading, values]);

  const processPayment = useCallback(async () => {
    setLoading(true);
    const pocket = new SQSPocketResponse(quoteRawResponse);
    const simplePlan: Coverages | undefined = find(pocket.coverages, { id: 'simpleTermPlan' });
    //const totalPayablePremium = Number(pocket.sioPremium.totalPremium.value.toString().replace('S$', '')) || 1;
    const totalPayablePremium = (pocket.totalAftDisOriginalPremiumAmt) ? Helpers.toTwoDecimal(pocket.totalAftDisOriginalPremiumAmt) : Helpers.toTwoDecimal(pocket.totalOriginalPremiumAmt);
    const payload = new PaymentRequest({ amount: totalPayablePremium, productName: 'SIO', contractCode: simplePlan?.productCode, transactionId });

    console.debug(pocket, simplePlan);
    dispatch(updateCardHolderName(values['name']));
    dispatch(updateBankId(Number(values['bank'])));
    dispatch(updateOtherBank(values['others']));
    onSavePayment(simplePlan?.productCode || '', payload);

    // try {
    //   const response = await postPayment(payload);
    //   const paymentUrl = get(response, 'data');
    //   const params = new URLSearchParams(paymentUrl.split('?')[1]);
    //   const refNo = params.get('referenceNumber');
    //   console.debug('processPayment', { paymentUrl, params, refNo } );
    //   dispatch(updateReferenceNo(String(refNo)));
    //   dispatch(updateCardHolderName(values['name']));
    //   dispatch(updateBankId(Number(values['bank'])));
    //   dispatch(updateOtherBank(values['others']));
    //   const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + '#/sio/payment?status=processing';
    //   console.log(fullPath);
    //   setLoading(false);
    //   window.open(paymentUrl, '_self');
    // } catch (error) {
    //   setLoading(false);
    //   console.error(error);
    //   setError(true);
    // }
  }, [ setLoading, dispatch, values, quoteRawResponse, onSavePayment ]);


  const onClickConfirmAndPay = async () => {
    if (isPaymentClick) return;
    setIsPaymentClick(true);
    setTimeout(() => {
      setIsPaymentClick(false);
    }, 5000);

    if (! await validatePromoCode()) {
      toggleModalPromoCode();
      return;
    }
    await processPayment();

  };

  const toggleModal = () => { setIsShow(!isShow) };
  const toggleModalPromoCode = () => { setIsShowPromocode(!isShowPromoCode) };

  const onEditQuote = () => {
    history.push('/sio/coverage');
  };

  const onBack = () => {
    if (hasError) {
      setError(false);
    } else {
      history.push('/sio/review');
    }
  };

  const gotoContact = () => {
    history.push('/sio/contact');

  }

  const backToAviva = () => {
    const website = get(window, 'AVIVA_VARIABLE_CONFIG.avivaWebsiteUrl');
    window.open(website, '_self');
  }
  
  useEffect(() => {
    if (!isStep4Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
      history.push('/sio/declaration');
    } else if (paymentDone) {
      history.push('/sio/thank-you');
    }
    const quotation: SQSPocketResponse = new SQSPocketResponse(sqsQuotation);
    const pocket = new SQSPocketResponse(quoteRawResponse);
    const quote = pocket.sioQuote;
    const premium = pocket.sioPremium;
    setPremium(premium);
    setQuotes(quote);
    checkStatus();
  }, [sqsQuotation, checkStatus, quoteRawResponse, history, isStep4Completed, paymentDone]);
  return (
    <Container>
      {isProcessing && (
        <ContainerBlock className={fluidBox} background={theme.colors.secondary} padding="40px 0" paddingTablet="130px 0 110px 30px">
          <ContainerGrid>
            <FullGrid>
              <LeftGrid>
                <Heading fontWeight={600} color={theme.colors.white} margin="0 0 10px">Thank you! </Heading>
                <Text fontSize={theme.fontSize.label} fontWeight={300} color={theme.colors.white}>Your application has been submitted. We’ll notify you on the status of your application within 5 working days.</Text>
              </LeftGrid>
            </FullGrid>
          </ContainerGrid>
        </ContainerBlock>
      )}
      <ContainerGrid>
        {!hasError ?
          (
            <React.Fragment>
              {!isProcessing && (<>
              <FullGrid>
                <MultiSteps margin="0 0 20px" steps={5} step={5} label="Payment" />
                <StickyBox>
                  <PremiumCost {...premium} />
                </StickyBox>
                <Heading margin="0 0 30px"> Premium payment </Heading>
              </FullGrid>
              <LeftGrid>
                <InformationBox hasClose>
                  <Text>The payment mode for the initial premium will apply to subsequent premiums.</Text>
                </InformationBox>

            
                <ContainerBlock background={theme.colors.grey[200]} padding="30px 20px 20px" paddingTablet={30}>
                  <Heading variant="sub">Credit Card payment details</Heading>
                  <Divider size={1} margin="0 0 30px" />

                  <Text margin="0 0 30px" fontSize={theme.fontSize.smallTitle} fontWeight={300}>
                    Total amount outstanding: <BoldText color={theme.colors.grey[300]}>{premium?.totalPremium?.value}</BoldText>
                  </Text>
                  <Heading variant="label">Card holder&apos;s name</Heading>
                  {!errors?.name?.isValid && errors?.name?.message && <Bullet variant="error">{errors?.name?.message}</Bullet>}
                  <Input margin="0 0 30px" name="name" type="alphanumeric" invalid={!isEmpty(errors?.name?.message)} value={values['name']} maxLength={30} onChange={handleChange} />
                  <Heading variant="label">Card holder&apos;s NRIC/FIN</Heading>
                  <Input margin="0 0 30px" disabled name="no" value={values['no']} />
                  <Heading variant="label">Credit card issuing bank</Heading>
                  <Dropdown source={banks} bindLabel="description" bindId="code" name="bank" value={values['bank']} onChange={handleChange} placeholder="Please select" margin="0 0 30px" />
                  {values['bank'] === '11' && (
                    <React.Fragment>
                      <Heading variant="label"> Others </Heading>
                      {!errors.others.isValid && errors.others.message && <Bullet variant="error">{errors.others.message}</Bullet>}
                      <Input name="others" value={values['others']} invalid={!isEmpty(errors.others.message)} onChange={handleChange} maxLength={30} />
                    </React.Fragment>
                  )}
                  <CheckboxList margin={0}>
                    <Checkbox checked={values['isAuthorised']} name="isAuthorised" onChange={handleChange}><Text>I authorise Singapore Life Ltd. to charge the <b>initial and subsequent</b> premium(s) to my credit card account for this insurance policy.</Text></Checkbox>
                  </CheckboxList>
                </ContainerBlock>
                <InformationBox hasClose margin="0 0 40px">
                    <Text fontWeight={600}>Please ensure you&apos;ve provided your own credit card details.</Text>
                    <Text>If you’re unable to make payment using your own credit card, please leave your contact details <Link variant="button" onClick={gotoContact} text="here"/> and our financial adviser representative will be in touch with you shortly.</Text>
                </InformationBox>
                <Button margin="0 0 20px" label="Confirm and pay" disabled={!formValid} onClick={onClickConfirmAndPay} />
                <Button margin="0 0 40px" label="Back" variant='secondary' onClick={onBack} />
              </LeftGrid>

              <EmptyGrid></EmptyGrid>
              <RightGrid>
                {quotes && <Quote onEdit={toggleModal} details={quotes} />}
                <ContactBox items={contactInformation} />
              </RightGrid>
              </>)}
            </React.Fragment>
          ) :
          (
            <React.Fragment>
              <LeftGrid>
                <InformationBox hasClose variant="error">
                  <Text>Payment is unsuccessful</Text>
                </InformationBox>
                <Heading>Oops...</Heading>
                <Heading margin="0 0 30px" variant="label">There seems to be a problem with your payment, and your purchase is incomplete.</Heading>
                <Heading margin="0 0 30px" variant="label">You may wish to check your credit card details or select another payment option.</Heading>
                <Text margin="0 0 40px">
                  Alternatively, you can contact our Customer Service Centre at <Link text="+65 6827 9933" />, Mondays to Fridays between 8.45am and 5.30pm for assistance.
                </Text>
                <Button margin="0 0 20px" label="Try again" onClick={onBack} />
                <Button margin="0 0 40px" label="Back to website" variant='secondary' onClick={backToAviva} />
              </LeftGrid>
              <EmptyGrid></EmptyGrid>
              <RightGrid>
                <ContactBox items={contactInformation} />
              </RightGrid>
            </React.Fragment>
          )
        }

        <Modal show={isShow} onClose={toggleModal}>
          <Heading fontWeight={600} variant="smallTitle" margin="0 0 20px" marginTablet="0 0 30px">Modify quote</Heading>
          <Text margin="0 0 27px">You will need to answer the underwriting questions once again if there is a change from the initial quotation.</Text>
          <Button margin="0 0 20px" label="Proceed" onClick={onEditQuote}></Button>
          <Button margin={0} label="Cancel" variant="secondary" onClick={toggleModal}></Button>
        </Modal>
        <Modal show={isShowPromoCode} onClose={toggleModalPromoCode }>
          <Heading fontWeight={600} variant="smallTitle" margin="0 0 20px" marginTablet="0 0 30px">Invalid promo code</Heading>
          <Text margin="0 0 30px" >You are not eligible for the promo code. Please return to the quotation page to remove the promo code. </Text>
          <Text margin="0 0 30px" >Alternatively, you may leave your contact details <Link text="here" variant="button" onClick={gotoContact} /> and our financial adviser representative will be in touch with you shortly.</Text>
          <Button margin="0 0 20px" label="Proceed to edit promo code" onClick={onEditQuote}></Button>
        </Modal>
      </ContainerGrid>
    </Container>
  );
};

export default Payment;

